import React, { useState } from 'react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import api from '../../api';
import { formatNumber, toExportNumber } from '../../utils/formatters';

const QUARTERS = [
  { value: 1, label: 'Q1 (Jan - Mar)' },
  { value: 2, label: 'Q2 (Apr - Jun)' },
  { value: 3, label: 'Q3 (Jul - Sep)' },
  { value: 4, label: 'Q4 (Oct - Dec)' }
];

const VATScheduled02Report = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchScheduledReport = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/vat-schedule-02?year=${year}&quarter=${quarter}`);
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching Scheduled 02 report:', error);
      alert('Failed to fetch Scheduled 02 report');
    } finally {
      setLoading(false);
      setHasSearched(true);
    }
  };

  const totalNetAmount = rows.reduce((sum, item) => sum + parseFloat(item.net_amount || 0), 0);
  const totalVATAmount = rows.reduce((sum, item) => sum + parseFloat(item.vat_amount || 0), 0);
  const totalDisallowableVAT = rows.reduce((sum, item) => sum + parseFloat(item.disallowable_vat_amount || 0), 0);

  const downloadExcel = () => {
    if (rows.length === 0) {
      alert('No data to export. Please generate the report first.');
      return;
    }

    const worksheetData = [
      ['Invoice Date', 'Invoice No', 'Supplier TIN Number', 'Name of Supplier', 'Description',
        'Net Amount (Rs.)', 'VAT Amount (Rs.)', 'Disallowable VAT Amount (Rs.)']
    ];

    rows.forEach((item) => {
      worksheetData.push([
        item.invoice_date ? format(new Date(item.invoice_date), 'MM/dd/yyyy') : 'N/A',
        item.invoice_no,
        item.supplier_tin,
        item.supplier_name,
        item.description,
        toExportNumber(item.net_amount, 2),
        toExportNumber(item.vat_amount, 2),
        toExportNumber(item.disallowable_vat_amount, 2)
      ]);
    });

    worksheetData.push([]);
    worksheetData.push([
      'TOTAL', '', '', '', '',
      toExportNumber(totalNetAmount, 2),
      toExportNumber(totalVATAmount, 2),
      toExportNumber(totalDisallowableVAT, 2)
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    worksheet['!cols'] = [
      { wch: 16 }, { wch: 18 }, { wch: 22 }, { wch: 28 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 24 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Scheduled 02');
    XLSX.writeFile(workbook, `scheduled-02-report-${year}-Q${quarter}.xlsx`);
  };

  return (
    <div>
      <div className="filters-section card" style={{ display: 'flex', gap: 'var(--spacing-lg)', padding: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="report-filter-group">
          <label className="report-filter-label">Year</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="report-filter-input"
            style={{ width: '110px' }}
          />
        </div>

        <div className="report-filter-group">
          <label className="report-filter-label">Quarter</label>
          <select
            value={quarter}
            onChange={(e) => setQuarter(parseInt(e.target.value, 10))}
            className="report-filter-select"
          >
            {QUARTERS.map((q) => (
              <option key={q.value} value={q.value}>{q.label}</option>
            ))}
          </select>
        </div>

        <div className="report-filter-actions" style={{ display: 'flex', gap: '10px' }}>
          <button onClick={fetchScheduledReport} disabled={loading} className="btn btn-primary">
            {loading ? (
              <>
                <div className="loading-spinner"></div>
                Generating...
              </>
            ) : (
              'Generate Scheduled 02'
            )}
          </button>
          {rows.length > 0 && (
            <button onClick={downloadExcel} className="btn btn-success">
              <span>📊</span> Export to Excel
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-state" role="status" aria-live="polite">
          <div className="loading-spinner"></div>
          Loading Scheduled 02 report data...
        </div>
      ) : rows.length > 0 ? (
        <>
          <div className="card" style={{ padding: 'var(--spacing-lg)' }}>
            <div style={{ overflowX: 'auto' }}>
            <table className="report-table">
              <colgroup>
                <col style={{ width: '130px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '200px' }} />
                <col style={{ width: '150px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '220px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="text-left">Invoice Date</th>
                  <th className="text-left">Invoice No</th>
                  <th className="text-left">Supplier TIN Number</th>
                  <th className="text-left">Name of Supplier</th>
                  <th className="text-left">Description</th>
                  <th className="text-right">Net Amount (Rs.)</th>
                  <th className="text-right">VAT Amount (Rs.)</th>
                  <th className="text-right">Disallowable VAT Amount (Rs.)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.id}>
                    <td className="text-left">{item.invoice_date ? format(new Date(item.invoice_date), 'dd/MM/yyyy') : 'N/A'}</td>
                    <td className="text-left">{item.invoice_no}</td>
                    <td className="text-left">{item.supplier_tin}</td>
                    <td className="text-left">{item.supplier_name}</td>
                    <td className="text-left">{item.description}</td>
                    <td className="report-table-value-numeric">{formatNumber(item.net_amount, 2, 2)}</td>
                    <td className="report-table-value-numeric text-primary">{formatNumber(item.vat_amount, 2, 2)}</td>
                    <td className="report-table-value-numeric text-danger">{formatNumber(item.disallowable_vat_amount, 2, 2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="5" className="text-right"><strong>TOTAL</strong></td>
                  <td className="report-table-value-numeric"><strong>{formatNumber(totalNetAmount, 2, 2)}</strong></td>
                  <td className="report-table-value-numeric text-primary"><strong>{formatNumber(totalVATAmount, 2, 2)}</strong></td>
                  <td className="report-table-value-numeric text-danger"><strong>{formatNumber(totalDisallowableVAT, 2, 2)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
          </div>
        </>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--spacing-3xl)', color: 'var(--color-text-secondary)' }}>
          <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-md)', opacity: 0.5 }}>📊</div>
          <h3>No Data Available</h3>
          <p>{hasSearched ? 'No invoices were scheduled for this Year/Quarter.' : 'Select a Year and Quarter, then generate the report.'}</p>
        </div>
      )}
    </div>
  );
};

export default VATScheduled02Report;
